import {
    CustomListItemId,
    DefaultReferences,
    GENERIC_REFERRAL_INTERNET_SENTINEL,
    InboundLeadClientType,
    Maybe,
    ReferenceBusinessId,
    ReferenceMap,
    RegionId,
    resolveStartingStatus,
    statusListKeyForInboundLeadClientType,
    UserId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import {
    ReferralSourcePicker,
    toMutationInput,
} from "components/references/select-reference/referral-source-picker";
import { trpc } from "components/rpc/trpc";
import { CopyableLink } from "components/shared/copyable-link";
import { CustomListSelect } from "components/shared/custom-list-select";
import { UserSelect } from "components/shared/user-select";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { BlankSlate } from "components/ui/blank-slate";
import { ErrorMessage } from "components/ui/error-message";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { MultiUserSelect } from "components/ui/multi-user-select";
import { PrimaryButton } from "components/ui/primary-button";
import { PrimaryPlusButton } from "components/ui/primary-plus-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { useGetUsersForRegionQuery } from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";
import { useProductName } from "store/selectors/branding";
import { useCustomList } from "store/selectors/bridge";
import {
    useCurrentRegion,
    useFeature,
    useIsMultiRegionUser,
    useRegionId,
} from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";
import { handleEvent } from "util/user-events";
import {
    LEAD_SOURCES_DESCRIPTION,
    LeadSourceRecentClients,
    LeadSourceStats,
    LeadSourceSummary,
    LeadSourceUnconvertedEmails,
} from "./lead-source-display";
import { StartingStatusDisplay, startingStatusDisplay } from "./lead-source-status";

const MAX_NAME_LENGTH = 200;
const NAME_PLACEHOLDER = "e.g. the vendor or website this mail comes from";
const UNKNOWN_ORGANIZATION = "Unknown organization";
const UNKNOWN_USER = "Unknown user";
const PICKER_EXCLUDED_TABS = [
    "referralContact",
    "communityContact",
    "referralCommunity",
] as const;

const defaultAttributionLabel = (): string => {
    const fallback = DefaultReferences.find(
        reference => reference.key === GENERIC_REFERRAL_INTERNET_SENTINEL,
    );
    return `${fallback?.text ?? GENERIC_REFERRAL_INTERNET_SENTINEL} (default)`;
};

type RouterOutputs = inferRouterOutputs<AppRouter>;
type LeadSource = RouterOutputs["inboundLeadSource"]["list"][number];
type ReferralSourceValue =
    | { kind: "none" }
    | { kind: "token"; token: string }
    | { kind: "business"; businessId: ReferenceBusinessId };

const NO_REFERRAL_SOURCE: ReferralSourceValue = { kind: "none" };

interface LeadSourceFormValues {
    name: string;
    clientType: InboundLeadClientType;
    statusListItemId: CustomListItemId;
    referralSource: ReferralSourceValue;
    assignedUserId: UserId | null;
    notifyUserIds: UserId[];
}

const useDefaultStatusListItemId = (
    clientType: InboundLeadClientType,
): CustomListItemId | null => {
    const { customList } = useCustomList(
        statusListKeyForInboundLeadClientType(clientType),
    );
    return useMemo(
        () => resolveStartingStatus(customList?.items ?? [], clientType, null),
        [clientType, customList],
    );
};

const useStatusDisplay = (source: LeadSource): StartingStatusDisplay => {
    const { customList } = useCustomList(
        statusListKeyForInboundLeadClientType(source.clientType),
    );
    return useMemo(
        () =>
            startingStatusDisplay(
                customList?.items ?? [],
                source.clientType,
                source.statusListItemId,
            ),
        [customList, source.clientType, source.statusListItemId],
    );
};

const clientTypeLabel = (clientType: InboundLeadClientType): string => {
    switch (clientType) {
        case "placement":
            return "Placement";
        case "homeCare":
            return "Home care";
        default: {
            const exhaustiveCheck: never = clientType;
            throw new Error(`Unhandled client type: ${exhaustiveCheck}`);
        }
    }
};

const referralSourceOf = (source: LeadSource): ReferralSourceValue => {
    if (source.referralBusinessId) {
        return { kind: "business", businessId: source.referralBusinessId };
    }
    if (source.referralToken) {
        return { kind: "token", token: source.referralToken };
    }
    return NO_REFERRAL_SOURCE;
};

const toReferenceMap = (value: ReferralSourceValue): ReferenceMap => ({
    identifier: value.kind === "token" ? Maybe.fromValue(value.token) : Maybe.none(),
    business:
        value.kind === "business" ? Maybe.fromValue(value.businessId) : Maybe.none(),
    contact: Maybe.none(),
});

const fromReferenceMap = (map: ReferenceMap): ReferralSourceValue => {
    const picked = toMutationInput(map);
    if (picked.kind === "business") {
        return {
            kind: "business",
            businessId: picked.businessId as ReferenceBusinessId,
        };
    }
    if (picked.kind === "token") {
        return { kind: "token", token: picked.token };
    }
    return NO_REFERRAL_SOURCE;
};

const describeReferralSource = (source: LeadSource): string => {
    if (source.referralBusinessId) {
        return source.referralBusinessName ?? UNKNOWN_ORGANIZATION;
    }
    if (source.referralToken) {
        return (
            DefaultReferences.find(reference => reference.key === source.referralToken)
                ?.text ?? source.referralToken
        );
    }
    return defaultAttributionLabel();
};

export const AgencySettingsLeadSources: React.FC<unknown> = () => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const regionId = useRegionId();
    const region = useCurrentRegion();
    const isMultiRegion = useIsMultiRegionUser();
    const homeCareEnabled = useFeature("homeCarePrototype");
    const placementIntakeEnabled = useFeature("placementLeadIntake");
    const availableClientTypes = useMemo<InboundLeadClientType[]>(() => {
        const types: InboundLeadClientType[] = [];
        if (placementIntakeEnabled) {
            types.push("placement");
        }
        if (homeCareEnabled) {
            types.push("homeCare");
        }
        return types;
    }, [homeCareEnabled, placementIntakeEnabled]);
    const defaultClientType: InboundLeadClientType =
        availableClientTypes[0] ?? "homeCare";
    const headerText =
        isMultiRegion && region ? `Lead Sources - ${region.name}` : "Lead Sources";

    const sourcesQuery = useQuery({
        ...trpcClient.inboundLeadSource.list.queryOptions({ regionId }),
        enabled: !!regionId,
    });
    const usersQuery = useGetUsersForRegionQuery(
        { regionId },
        { ...stableQueryOptions(), enabled: !!regionId },
    );

    const userNameById = useMemo(() => {
        const names = new Map<UserId, string>();
        for (const user of usersQuery.data?.getUsersForRegion ?? []) {
            names.set(user.id, user.preferredName);
        }
        return names;
    }, [usersQuery.data?.getUsersForRegion]);

    const refetchSources = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: [["inboundLeadSource"]] });
    }, [queryClient]);

    if (sourcesQuery.isPending) {
        return <Spinner />;
    }

    if (sourcesQuery.isError) {
        return (
            <ErrorMessage component="AgencySettingsLeadSources">
                <pre>{JSON.stringify(sourcesQuery.error, null, 2)}</pre>
            </ErrorMessage>
        );
    }

    return (
        <ContentFull>
            <ContentHeader>{headerText}</ContentHeader>
            <ContentSubheader>{LEAD_SOURCES_DESCRIPTION}</ContentSubheader>
            <div className="max-w-3xl space-y-6">
                {sourcesQuery.data.length === 0 ? (
                    <BlankSlate>
                        You don&apos;t have any lead sources yet. Add your first one
                        below.
                    </BlankSlate>
                ) : null}
                {sourcesQuery.data.map(source => (
                    <LeadSourceCard
                        key={source.id}
                        source={source}
                        userNameById={userNameById}
                        availableClientTypes={availableClientTypes}
                        onChanged={refetchSources}
                    />
                ))}
                <AddLeadSourceRow
                    availableClientTypes={availableClientTypes}
                    defaultClientType={defaultClientType}
                    onCreated={refetchSources}
                />
            </div>
        </ContentFull>
    );
};

interface LeadSourceCardProps {
    source: LeadSource;
    userNameById: Map<UserId, string>;
    availableClientTypes: InboundLeadClientType[];
    onChanged: () => Promise<void>;
}

const LeadSourceCard: React.FC<LeadSourceCardProps> = props => {
    const { source, userNameById, availableClientTypes, onChanged } = props;
    const trpcClient = trpc.useTRPC();
    const [isEditing, setIsEditing] = useState(false);
    const removeMutation = useMutation({
        ...trpcClient.inboundLeadSource.remove.mutationOptions(),
    });
    const updateMutation = useMutation({
        ...trpcClient.inboundLeadSource.update.mutationOptions(),
    });

    const remove = useCallback(async () => {
        await removeMutation.mutateAsync({ id: source.id });
        handleEvent("lead-source-delete");
        await onChanged();
    }, [onChanged, removeMutation, source.id]);

    const stopEditing = useCallback(() => setIsEditing(false), []);
    const startEditing = useCallback(() => setIsEditing(true), []);

    const save = useCallback(
        async (values: LeadSourceFormValues) => {
            await updateMutation.mutateAsync({ id: source.id, ...values });
            handleEvent("lead-source-update");
            await onChanged();
            stopEditing();
        },
        [onChanged, source.id, stopEditing, updateMutation],
    );

    const status = useStatusDisplay(source);
    const assigneeName = source.assignedUserId
        ? (userNameById.get(source.assignedUserId) ?? UNKNOWN_USER)
        : "Unassigned";
    const notifyNames =
        source.notifyUserIds
            .map(userId => userNameById.get(userId) ?? UNKNOWN_USER)
            .join(", ") || "No one";

    return (
        <section className="rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2.5">
                <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
                    {source.name}
                </h3>
                {isEditing ? null : (
                    <>
                        <EditButton backgroundColor="bg-white" onClick={startEditing} />
                        <DeleteButton
                            backgroundColor="bg-white"
                            confirm={{
                                title: "Delete lead source?",
                                message: source.emailAddress
                                    ? `Emails sent to ${source.emailAddress} will stop becoming clients.`
                                    : "Emails sent to this source will stop becoming clients.",
                                confirmLabel: "Delete",
                            }}
                            onClick={remove}
                        />
                    </>
                )}
            </div>
            <div className="space-y-4 p-4">
                {isEditing ? (
                    <LeadSourceForm
                        regionId={source.regionId}
                        availableClientTypes={availableClientTypes}
                        initialValues={{
                            name: source.name,
                            clientType: source.clientType,
                            statusListItemId: source.statusListItemId,
                            referralSource: referralSourceOf(source),
                            assignedUserId: source.assignedUserId,
                            notifyUserIds: source.notifyUserIds,
                        }}
                        isPending={updateMutation.isPending}
                        errorMessage={
                            updateMutation.isError
                                ? "The lead source could not be saved. Please try again."
                                : null
                        }
                        onSubmit={save}
                        onCancel={stopEditing}
                    />
                ) : (
                    <>
                        {availableClientTypes.includes(source.clientType) ? null : (
                            <InlineBanner type="warning">
                                {clientTypeLabel(source.clientType)} lead intake is not
                                enabled, so emails sent to this source will not become
                                clients.
                            </InlineBanner>
                        )}
                        {status.replacedBy ? (
                            <InlineBanner type="warning">
                                The status this source used is no longer available, so new
                                clients will start in {status.replacedBy}. Edit the source
                                to choose another status.
                            </InlineBanner>
                        ) : null}
                        <LeadSourceSummary
                            clientType={
                                availableClientTypes.length > 1 ||
                                !availableClientTypes.includes(source.clientType)
                                    ? clientTypeLabel(source.clientType)
                                    : null
                            }
                            attribution={describeReferralSource(source)}
                            status={status.name}
                            assignee={assigneeName}
                            notify={notifyNames}
                        />
                        <LeadSourceIntakeAddress source={source} />
                        <LeadSourceStats
                            emailsReceived={source.stats.emailsReceived}
                            leadsCreated={source.stats.leadsCreated}
                            lastEmailAt={source.stats.lastEmailAt}
                        />
                        <LeadSourceRecentClients
                            recentlyCreated={source.recentlyCreated}
                        />
                        <LeadSourceUnconvertedEmails
                            recentUnconverted={source.recentUnconverted}
                        />
                        {removeMutation.isError ? (
                            <InlineBanner type="error">
                                The lead source could not be deleted. Please try again.
                            </InlineBanner>
                        ) : null}
                    </>
                )}
            </div>
        </section>
    );
};

interface LeadSourceIntakeAddressProps {
    source: LeadSource;
}

const LeadSourceIntakeAddress: React.FC<LeadSourceIntakeAddressProps> = props => {
    const { source } = props;
    const productName = useProductName();

    if (!source.emailAddress) {
        return (
            <InlineBanner type="warning">
                Lead intake email is not configured on this server, so this source does
                not have an address yet.
            </InlineBanner>
        );
    }

    return (
        <div className="space-y-3">
            <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Intake address</p>
                <CopyableLink url={source.emailAddress} copiedLabel="Address copied!" />
            </div>
            <div className="space-y-1 text-sm text-gray-700">
                <p>To send leads into {productName}, choose one of these options:</p>
                <ul className="list-disc space-y-1 pl-5">
                    <li>
                        Set up an email forwarding rule to automatically forward lead
                        source emails to the intake address.
                    </li>
                    <li>
                        Configure your lead source settings to send its new lead
                        notifications to the intake address.
                    </li>
                </ul>
            </div>
        </div>
    );
};

type LeadSourceFormInitialValues = Omit<LeadSourceFormValues, "statusListItemId"> & {
    statusListItemId: CustomListItemId | null;
};

interface LeadSourceFormProps {
    regionId: RegionId;
    availableClientTypes: InboundLeadClientType[];
    initialValues: LeadSourceFormInitialValues;
    isPending: boolean;
    errorMessage: string | null;
    onSubmit: (values: LeadSourceFormValues) => Promise<void>;
    onCancel: () => void;
}

const LeadSourceForm: React.FC<LeadSourceFormProps> = props => {
    const {
        regionId,
        availableClientTypes,
        initialValues,
        isPending,
        errorMessage,
        onSubmit,
        onCancel,
    } = props;
    const [name, setName] = useState(initialValues.name);
    const [clientType, setClientType] = useState<InboundLeadClientType>(
        initialValues.clientType,
    );
    const [statusListItemId, setStatusListItemId] = useState<CustomListItemId | null>(
        initialValues.statusListItemId,
    );
    const [referralSource, setReferralSource] = useState<ReferralSourceValue>(
        initialValues.referralSource,
    );
    const [assignedUserId, setAssignedUserId] = useState<UserId | null>(
        initialValues.assignedUserId,
    );
    const [notifyUserIds, setNotifyUserIds] = useState<UserId[]>(
        initialValues.notifyUserIds,
    );
    const [wasSubmitted, setWasSubmitted] = useState(false);

    const handleReferralSourceChange = useCallback(
        (map: ReferenceMap) => setReferralSource(fromReferenceMap(map)),
        [],
    );

    const handleClientTypeChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            setClientType(e.target.value as InboundLeadClientType);
            setStatusListItemId(null);
        },
        [],
    );

    const handleStatusChange = useCallback(
        (value: string | null) =>
            setStatusListItemId((value as CustomListItemId) || null),
        [],
    );

    const defaultStatusId = useDefaultStatusListItemId(clientType);
    const effectiveStatusId = statusListItemId ?? defaultStatusId;

    const selectableClientTypes = useMemo<InboundLeadClientType[]>(
        () =>
            availableClientTypes.includes(initialValues.clientType)
                ? availableClientTypes
                : [...availableClientTypes, initialValues.clientType],
        [availableClientTypes, initialValues.clientType],
    );

    const trimmedName = name.trim();
    const isIncomplete =
        trimmedName.length === 0 || referralSource.kind === "none" || !effectiveStatusId;

    const submit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            setWasSubmitted(true);
            if (isIncomplete) {
                return;
            }
            await onSubmit({
                name: trimmedName,
                clientType,
                statusListItemId: effectiveStatusId,
                referralSource,
                assignedUserId,
                notifyUserIds,
            });
        },
        [
            assignedUserId,
            clientType,
            effectiveStatusId,
            isIncomplete,
            notifyUserIds,
            onSubmit,
            referralSource,
            trimmedName,
        ],
    );

    return (
        <form onSubmit={submit} className="space-y-3">
            <Input
                label="Name"
                placeholder={NAME_PLACEHOLDER}
                autoFocus
                maxLength={MAX_NAME_LENGTH}
                value={name}
                onChange={e => setName(e.target.value)}
            />
            {wasSubmitted && trimmedName.length === 0 ? (
                <p className="text-sm text-red-600">Enter a name before saving.</p>
            ) : null}
            {selectableClientTypes.length > 1 ? (
                <Select
                    label="Client type"
                    value={clientType}
                    onChange={handleClientTypeChange}>
                    {selectableClientTypes.map(type => (
                        <option key={type} value={type}>
                            {clientTypeLabel(type)}
                        </option>
                    ))}
                </Select>
            ) : null}
            <CustomListSelect
                label="Status for new clients"
                customListKey={statusListKeyForInboundLeadClientType(clientType)}
                value={effectiveStatusId}
                onChange={handleStatusChange}
                valueMode="id"
            />
            <ReferralSourcePicker
                label="Attribute clients to a referral source"
                showIcon={false}
                variant={{ kind: "compact" }}
                clearing="disallowed"
                value={toReferenceMap(referralSource)}
                onChange={handleReferralSourceChange}
                createOptions={["organization"]}
                exclude={[...PICKER_EXCLUDED_TABS]}
            />
            {wasSubmitted && referralSource.kind === "none" ? (
                <p className="text-sm text-red-600">
                    Choose a referral source before saving.
                </p>
            ) : null}
            <UserSelect
                label="Assign new clients to"
                value={assignedUserId}
                onChange={userId => setAssignedUserId((userId as UserId) || null)}
                includeEveryone={false}
                includeUnassigned={true}
            />
            <MultiUserSelect
                label="Email these users about new clients"
                regionId={regionId}
                value={notifyUserIds}
                onChange={setNotifyUserIds}
            />
            {errorMessage ? (
                <InlineBanner type="error">{errorMessage}</InlineBanner>
            ) : null}
            <div className="flex justify-end space-x-2">
                <SecondaryButton type="button" onClick={onCancel}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={isPending}>
                    {isPending ? <Spinner /> : <span>Save</span>}
                </PrimaryButton>
            </div>
        </form>
    );
};

interface AddLeadSourceRowProps {
    availableClientTypes: InboundLeadClientType[];
    defaultClientType: InboundLeadClientType;
    onCreated: () => Promise<void>;
}

const emptyFormValues = (
    clientType: InboundLeadClientType,
): LeadSourceFormInitialValues => ({
    name: "",
    clientType,
    statusListItemId: null,
    referralSource: NO_REFERRAL_SOURCE,
    assignedUserId: null,
    notifyUserIds: [],
});

const AddLeadSourceRow: React.FC<AddLeadSourceRowProps> = props => {
    const { availableClientTypes, defaultClientType, onCreated } = props;
    const trpcClient = trpc.useTRPC();
    const regionId = useRegionId();
    const [isAdding, setIsAdding] = useState(false);
    const createMutation = useMutation({
        ...trpcClient.inboundLeadSource.create.mutationOptions(),
    });

    const startAdding = useCallback(() => setIsAdding(true), []);
    const stopAdding = useCallback(() => setIsAdding(false), []);

    const create = useCallback(
        async (values: LeadSourceFormValues) => {
            await createMutation.mutateAsync({ regionId, ...values });
            handleEvent("lead-source-create");
            stopAdding();
            await onCreated();
        },
        [createMutation, onCreated, regionId, stopAdding],
    );

    if (!isAdding) {
        return (
            <PrimaryPlusButton onClick={startAdding}>Add lead source</PrimaryPlusButton>
        );
    }

    return (
        <div className="space-y-3 rounded-lg border border-gray-300 bg-gray-50 p-4">
            <div>
                <h3 className="text-base font-semibold text-gray-900">New lead source</h3>
                <p className="text-sm text-gray-500">
                    The intake address is created when you save.
                </p>
            </div>
            <LeadSourceForm
                regionId={regionId}
                availableClientTypes={availableClientTypes}
                initialValues={emptyFormValues(defaultClientType)}
                isPending={createMutation.isPending}
                errorMessage={
                    createMutation.isError
                        ? "The lead source could not be added. Please try again."
                        : null
                }
                onSubmit={create}
                onCancel={stopAdding}
            />
        </div>
    );
};
